import { supabase } from "@/integrations/supabase/client";
import { invokeWithRetry } from './common/invokeWithRetry';

// Feature flags for progressive activation
const FEATURE_FLAGS = {
  ASSIGN_COACH: true,
  UNASSIGN_COACH: true,
  GET_COACH_ASSIGNMENTS: true,
};

export interface AssignCoachParams {
  class_booking_id: string;
  coach_id: string;
  gym_id: string;
}

export interface UnassignCoachParams {
  class_booking_id: string;
  coach_id: string;
  gym_id: string;
}

export interface GetCoachAssignmentsParams {
  gym_id: string;
  startDate: string;
  endDate: string;
  granularity?: "day" | "week" | "month";
  coachIds?: string[];
  page?: number;
  pageSize?: number;
}

export interface CoachAssignment {
  day?: string;
  week?: string;
  month?: string;
  coach_id: string | null;
  coach_first_name: string | null;
  coach_last_name: string | null;
  assignments: number | null;
  gym_id: string | null;
}

export interface GetCoachAssignmentsResult {
  data: CoachAssignment[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Assign a coach to a class booking
 */
export async function assignCoach(params: AssignCoachParams): Promise<boolean> {
  if (FEATURE_FLAGS.ASSIGN_COACH) {
    const { data, error, authExpired } = await invokeWithRetry("coach-assignments-management", {
      action: "assign_coach",
      ...params,
    });

    if (authExpired) {
      return false;
    }

    if (error) throw error;
    return data?.success ?? false;
  }

  // Fallback: direct database call
  const { error } = await supabase
    .from("class_booking_coach")
    .upsert(
      { class_booking_id: params.class_booking_id, coach_id: params.coach_id },
      { onConflict: "class_booking_id", ignoreDuplicates: true }
    );

  if (error) throw error;
  return true;
}

/**
 * Unassign a coach from a class booking
 */
export async function unassignCoach(params: UnassignCoachParams): Promise<boolean> {
  if (FEATURE_FLAGS.UNASSIGN_COACH) {
    const { data, error, authExpired } = await invokeWithRetry("coach-assignments-management", {
      action: "unassign_coach",
      ...params,
    });

    if (authExpired) {
      return false;
    }

    if (error) throw error;
    return data?.success ?? false;
  }

  // Fallback: direct database call
  const { error } = await supabase
    .from("class_booking_coach")
    .delete()
    .match({ class_booking_id: params.class_booking_id, coach_id: params.coach_id });

  if (error) throw error;
  return true;
}

/**
 * Get coach assignments for a gym and date range
 */
export async function getCoachAssignments(
  params: GetCoachAssignmentsParams
): Promise<GetCoachAssignmentsResult> {
  if (FEATURE_FLAGS.GET_COACH_ASSIGNMENTS) {
    const { data, error, authExpired } = await invokeWithRetry("coach-assignments-management", {
      action: "get_coach_assignments",
      ...params,
    });

    if (authExpired) {
      return { data: [], total: 0, page: params.page || 1, pageSize: params.pageSize || 100 };
    }

    if (error) throw error;
    return data as GetCoachAssignmentsResult;
  }

  // Fallback: direct database call
  const {
    gym_id,
    startDate,
    endDate,
    granularity = "day",
    coachIds,
    page = 1,
    pageSize = 100,
  } = params;

  const col = granularity;

  let query = supabase
    .from("coach_assignments_admin_v")
    .select("*", { count: "exact" })
    .eq("gym_id", gym_id)
    .gte(col, startDate)
    .lte(col, endDate)
    .order(col, { ascending: true })
    .order("coach_last_name", { ascending: true, nullsFirst: true });

  if (coachIds?.length) {
    query = query.in("coach_id", coachIds);
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const { data, error, count } = await query.range(from, to);

  if (error) throw error;

  return { data: data ?? [], total: count ?? 0, page, pageSize };
}
